import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { ArrowLeft, Truck, Hash } from 'lucide-react'
import { apiGet } from '../../lib/api'
import type { Order } from '@buddys-choice/shared'

type OrderDetail = Order & {
  items?: Array<{
    id: string
    productName: string
    variantDescription?: string | null
    quantity: number
    unitPrice: number
    lineTotal: number
  }>
}

function formatCurrency(amt: number) {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amt)
}

function formatOrderDate(value: string | undefined) {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('en-IN')
}

export const AdminOrderDetailPage: React.FC = () => {
  const { orderId } = useParams({ from: '/admin/orders/$orderId' })
  const navigate = useNavigate()
  const [order, setOrder] = useState<OrderDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    apiGet<OrderDetail>(`orders/${orderId}`)
      .then(setOrder)
      .catch((err: Error) => setError(err.message || 'Failed to load order.'))
      .finally(() => setLoading(false))
  }, [orderId])

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-8 h-8 border-2 border-[var(--gold-main)] border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (error || !order) {
    return (
      <div className="space-y-4">
        <Link to="/admin/orders" className="inline-flex items-center gap-1.5 text-xs text-[var(--text-muted)] hover:text-[var(--gold-main)]">
          <ArrowLeft className="w-4 h-4" /> Back to orders
        </Link>
        <div className="glass-luxury-card p-4 text-sm text-red-400 border border-red-500/30">{error || 'Order not found.'}</div>
      </div>
    )
  }

  return (
    <div className="space-y-5 sm:space-y-6 max-w-3xl">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <Link to="/admin/orders" className="inline-flex items-center gap-1.5 text-xs text-[var(--text-muted)] hover:text-[var(--gold-main)] min-h-[44px]">
          <ArrowLeft className="w-4 h-4" /> Back to orders
        </Link>
        <button
          type="button"
          onClick={() => navigate({ to: '/admin/orders/$orderId/status', params: { orderId } })}
          className="btn-gold text-xs py-2.5 px-5 flex items-center justify-center gap-1.5 min-h-[44px]"
        >
          <Truck className="w-4 h-4" /> Update status
        </button>
      </div>

      <div className="glass-luxury-card p-4 sm:p-6 space-y-4">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 border-b border-[var(--border)] pb-4">
          <div>
            <h1 className="text-lg sm:text-xl font-title font-bold text-[var(--gold-main)]">{order.orderNumber}</h1>
            <p className="text-xs text-[var(--text-muted)] mt-1">Placed on {formatOrderDate(order.createdAt)}</p>
            {order.trackingId && (
              <p className="text-[10px] font-mono text-[var(--text-faint)] mt-1 flex items-center gap-1">
                <Hash className="w-3 h-3" /> {order.trackingId}
              </p>
            )}
          </div>
          <span className={`badge badge-${order.status} self-start`}>
            {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs bg-[var(--bg-surface-2)]/60 p-4 rounded-xl border border-[var(--border)]">
          <div>
            <span className="text-[var(--text-muted)] block text-[10px] uppercase tracking-wider mb-1">Customer</span>
            <p className="font-semibold text-[var(--text-primary)]">{order.customerName}</p>
            <p className="font-mono text-[var(--gold-main)] mt-0.5">{order.customerPhone}</p>
          </div>
          <div>
            <span className="text-[var(--text-muted)] block text-[10px] uppercase tracking-wider mb-1">Shipping address</span>
            <p className="text-[var(--text-primary)]">{order.shippingAddressLine1}</p>
            <p className="text-[var(--text-muted)] mt-0.5">
              {order.shippingCity}, {order.shippingState} – {order.shippingPincode}
            </p>
          </div>
        </div>

        <div>
          <h2 className="text-[11px] font-bold uppercase tracking-wider text-[var(--gold-main)] mb-3">Ordered items</h2>
          <div className="space-y-2">
            {order.items?.map((item) => (
              <div key={item.id} className="p-3 rounded-xl bg-[var(--bg-surface-2)] text-xs flex justify-between items-center gap-3 border border-[var(--border)]/50">
                <div className="min-w-0">
                  <p className="font-medium text-[var(--text-primary)]">{item.productName}</p>
                  {item.variantDescription && (
                    <p className="text-[10px] text-[var(--gold-main)] mt-0.5">{item.variantDescription}</p>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <p className="font-bold text-[var(--text-primary)]">
                    {item.quantity} × {formatCurrency(item.unitPrice)}
                  </p>
                  <p className="text-[10px] text-[var(--gold-main)] font-mono">= {formatCurrency(item.lineTotal)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="pt-3 border-t border-[var(--border)] text-xs space-y-1.5">
          <div className="flex justify-between text-[var(--text-muted)]">
            <span>Subtotal</span>
            <span>{formatCurrency(order.subtotal)}</span>
          </div>
          <div className="flex justify-between text-[var(--text-muted)]">
            <span>CGST + SGST</span>
            <span>{formatCurrency(order.taxCgst + order.taxSgst)}</span>
          </div>
          <div className="flex justify-between text-[var(--gold-main)] font-title font-bold text-sm pt-1">
            <span>Total</span>
            <span>{formatCurrency(order.total)}</span>
          </div>
        </div>
      </div>
    </div>
  )
}
